/* The head of the document, owned by the route rather than by index.html.
 *
 * A single page app has one `<head>` and twenty six routes, so without this
 * every page a search engine or a messaging app looked at carried the same
 * title, the same description and no canonical at all. This file is the whole
 * mechanism: a pure function that turns a page's intentions into a list of
 * tags, a function that puts that list into a document, and a hook that runs
 * the second one for the life of a screen.
 *
 * **Three rules the tags obey, and they are checked in `tests/unit/head`.**
 * A title is at most 60 characters, because a longer one is cut off in a
 * result and the cut lands mid word. A description is between 120 and 155,
 * which is the band a result actually renders. A canonical is absolute and
 * carries no trailing slash, because `/pricing` and `/pricing/` are one page
 * and two URLs is how a page competes with itself.
 *
 * **No new dependency.** react-helmet and its successors exist and all of
 * them are a state library and a renderer for four kinds of tag. What is
 * needed here is `querySelector` and `setAttribute`, so that is what this is.
 *
 * **Everything is undone on unmount.** An attribute this file overwrote is
 * put back the way it was found, and an element it created is removed. That
 * matters more than it looks: the prerendered snapshots in
 * `scripts/prerender.mjs` are captured out of a live document, so the tags
 * this file wrote are in the HTML a crawler is served, and the app then boots
 * over the top of them and finds them again rather than doubling them up.
 */
import { useEffect } from "react";

/** The one origin this product is served from. Written once. */
export const SITE = "https://mathshub.me";

/** The card image, generated by `scripts/og.mjs` and served from our origin. */
export const OG_IMAGE = `${SITE}/og.png`;

export interface HeadSpec {
  /** "<Page> | MathsHub", at most 60 characters. */
  title: string;
  /** 120 to 155 characters. Absent on a route no one may index. */
  description?: string;
  /** A path, not a URL: "/pricing". Absent on a route no one may index. */
  canonical?: string;
  /** "index, follow, ..." or "noindex, nofollow". */
  robots?: string;
  /** Open Graph properties without the `og:` prefix, merged over the
   *  defaults below. `title`, `description` and `url` come from the fields
   *  above and do not need repeating here. */
  og?: Record<string, string>;
  /** Structured data. One `<script type="application/ld+json">` each. */
  jsonLd?: unknown[];
}

/** One tag, as a selector that finds it and the attributes it should carry.
 *  A description rather than an element, so the shape can be asserted on
 *  without a browser. */
export interface HeadTag {
  /** Always `tag[attr="value"]`, optionally twice. The fake document in the
   *  unit tests parses exactly this much of a selector, deliberately. */
  sel: string;
  tag: "meta" | "link" | "script";
  attrs: Record<string, string>;
  text?: string;
}

/** The Open Graph properties every page carries unless it says otherwise. */
const OG_DEFAULTS: Record<string, string> = {
  site_name: "MathsHub",
  type: "website",
  locale: "en_GB",
  image: OG_IMAGE,
  "image:width": "1200",
  "image:height": "630",
  "image:alt": "MathsHub. Homework help, less guesswork for busy parents.",
};

/**
 * The absolute canonical URL for a path.
 *
 * A trailing slash is taken off, because a route and the same route with a
 * slash are one page and Google will otherwise pick a winner itself. The root
 * keeps its slash: `https://mathshub.me` and `https://mathshub.me/` are the
 * same request and the second is the form every other tool writes.
 *
 * A path that is already absolute is returned untouched, so a caller that
 * knows better than this function can say so.
 */
export function canonicalUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  const trimmed = path.replace(/\/+$/, "");
  return trimmed === "" ? `${SITE}/` : `${SITE}${trimmed.startsWith("/") ? "" : "/"}${trimmed}`;
}

/**
 * The tags a spec asks for, in the order they are written.
 *
 * Pure, and that is the point: the rules about what a page advertises are
 * asserted against this list rather than against a rendered browser, and the
 * browser test then only has to prove that a list gets into a document.
 */
export function headTags(spec: HeadSpec): HeadTag[] {
  const tags: HeadTag[] = [];

  if (spec.description) {
    tags.push({
      sel: 'meta[name="description"]',
      tag: "meta",
      attrs: { name: "description", content: spec.description },
    });
  }
  if (spec.robots) {
    tags.push({
      sel: 'meta[name="robots"]',
      tag: "meta",
      attrs: { name: "robots", content: spec.robots },
    });
  }
  if (spec.canonical) {
    tags.push({
      sel: 'link[rel="canonical"]',
      tag: "link",
      attrs: { rel: "canonical", href: canonicalUrl(spec.canonical) },
    });
  }

  /* Open Graph. The title and the description are the page's own, never a
     second wording of them, so a link preview and a search result cannot
     disagree about what a page is. */
  const og: Record<string, string> = { ...OG_DEFAULTS, title: spec.title, ...spec.og };
  if (spec.description) og.description ??= spec.description;
  if (spec.canonical) og.url ??= canonicalUrl(spec.canonical);
  for (const [key, content] of Object.entries(og)) {
    tags.push({
      sel: `meta[property="og:${key}"]`,
      tag: "meta",
      attrs: { property: `og:${key}`, content },
    });
  }

  /* The Twitter card, which reads `og:` for everything except the card type
     and only needs its own tags where the two would differ. They do not, so
     these three carry the same strings. */
  const twitter: Record<string, string> = {
    card: "summary_large_image",
    title: og.title!,
    image: og.image!,
  };
  if (og.description) twitter.description = og.description;
  for (const [key, content] of Object.entries(twitter)) {
    tags.push({
      sel: `meta[name="twitter:${key}"]`,
      tag: "meta",
      attrs: { name: `twitter:${key}`, content },
    });
  }

  (spec.jsonLd ?? []).forEach((block, i) => {
    tags.push({
      sel: `script[type="application/ld+json"][data-head-id="ld-${i}"]`,
      tag: "script",
      attrs: { type: "application/ld+json", "data-head-id": `ld-${i}` },
      text: JSON.stringify(block),
    });
  });

  return tags;
}

/** The minimum of a document this file touches. Named so the unit tests can
 *  stand something small in for a browser and still be testing this code. */
export interface HeadElement {
  getAttribute(name: string): string | null;
  setAttribute(name: string, value: string): void;
  removeAttribute(name: string): void;
  remove(): void;
  textContent: string | null;
}

export interface HeadDocument {
  title: string;
  head: {
    querySelector(sel: string): HeadElement | null;
    querySelectorAll(sel: string): HeadElement[] | NodeListOf<Element>;
    appendChild(node: never): void;
  };
  createElement(tag: string): HeadElement;
}

/**
 * Put a spec into a document, and hand back the function that takes it out.
 *
 * An element that is already there is edited and its old attributes are
 * remembered. An element that is not is created, marked `data-head` and
 * appended. The undo runs in reverse, so a tag that was edited twice ends up
 * where it started rather than one step along.
 */
export function applyHead(doc: HeadDocument, spec: HeadSpec): () => void {
  const undo: (() => void)[] = [];

  const wasTitle = doc.title;
  doc.title = spec.title;
  undo.push(() => {
    doc.title = wasTitle;
  });

  const tags = headTags(spec);

  /* Structured data left behind by the route before this one. Each block is
     numbered, so anything numbered past the end of this route's list belongs
     to a page that is no longer on screen and would otherwise describe this
     one to a crawler. */
  const wanted = (spec.jsonLd ?? []).length;
  const stale = doc.head.querySelectorAll('script[type="application/ld+json"][data-head-id]');
  for (const node of Array.from(stale) as HeadElement[]) {
    const id = node.getAttribute("data-head-id") ?? "";
    const at = Number(id.replace("ld-", ""));
    if (!Number.isFinite(at) || at >= wanted) node.remove();
  }

  for (const tag of tags) {
    const found = doc.head.querySelector(tag.sel);
    if (found) {
      const before = Object.keys(tag.attrs).map(
        (key) => [key, found.getAttribute(key)] as const,
      );
      const beforeText = found.textContent;
      for (const [key, value] of Object.entries(tag.attrs)) found.setAttribute(key, value);
      if (tag.text !== undefined) found.textContent = tag.text;
      undo.push(() => {
        for (const [key, value] of before) {
          if (value === null) found.removeAttribute(key);
          else found.setAttribute(key, value);
        }
        if (tag.text !== undefined) found.textContent = beforeText;
      });
    } else {
      const made = doc.createElement(tag.tag);
      for (const [key, value] of Object.entries(tag.attrs)) made.setAttribute(key, value);
      if (tag.text !== undefined) made.textContent = tag.text;
      made.setAttribute("data-head", "");
      doc.head.appendChild(made as never);
      undo.push(() => made.remove());
    }
  }

  return () => {
    for (const step of undo.reverse()) step();
  };
}

/**
 * The hook. One call, in the layout above every route, and the spec changes
 * with the path.
 *
 * The effect keys off the serialised spec rather than the object, because a
 * screen builds a fresh object on every render and an object identity check
 * would rewrite the head on every keystroke in a form.
 */
export function useHead(spec: HeadSpec): void {
  const key = JSON.stringify(spec);
  useEffect(() => {
    if (typeof document === "undefined") return;
    return applyHead(document as unknown as HeadDocument, JSON.parse(key) as HeadSpec);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}
